import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { BodyNormal, H3, H5 } from '@atlassian/mpac-primitives';
import { css } from '@compiled/react';

import type { UseCaseCardProps } from './UseCaseCard';

const useCaseCardContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.500'),
  position: 'relative',
  padding: `5rem ${token('space.0')} ${token('space.600')}`,
  marginBottom: token('space.800'),
  maxWidth: '96rem',
  marginLeft: 'auto',
  marginRight: 'auto',

  '@media screen and (min-width: 110.5rem) and (max-width: 119.9rem)': {
    marginLeft: '12rem',
    marginRight: '12rem',
  },

  '@media screen and (min-width: 90rem) and (max-width: 110.4rem)': {
    marginLeft: '9rem',
    marginRight: '9rem',
  },

  '&::before': {
    content: '',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: token('color.background.accent.gray.subtlest'),
    clipPath: 'polygon(0 0, 100% 8%, 100% 100%, 0 100%)',
    zIndex: '1',

    [media.below.sm]: {
      clipPath: 'polygon(0 0, 100% 3%, 100% 100%, 0 100%)',
    },
  },
});

const useCaseCardHeadingStyles = css({
  zIndex: '2',
  textAlign: 'center',
  padding: `${token('space.0')} ${token('space.500')}`,

  [media.below.sm]: {
    padding: `${token('space.0')} ${token('space.200')}`,
  },
});

const useCaseCardUseCasesContainerStyles = css({
  display: 'flex',
  gap: token('space.400'),
  justifyContent: 'center',
  padding: `${token('space.0')} ${token('space.500')}`,
  zIndex: '2',

  [media.below.md]: {
    gap: token('space.300'),
  },

  [media.above.md]: {
    padding: `${token('space.0')} 6.250rem`,
  },

  [media.below.sm]: {
    flexDirection: 'column',
    padding: `${token('space.0')} ${token('space.500')}`,
  },
});

const useCaseCardUseCaseStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  padding: `${token('space.300')}`,

  [media.below.md]: {
    padding: `${token('space.0')}`,
  },
});

const useCaseCardUseCaseTitleStyles = css({
  fontWeight: 653,
  lineHeight: token('space.300'),
});

const useCaseCardUseCaseDescriptionStyles = css({
  marginTop: token('space.0'),
});

export const UseCaseCardWithImage = ({ heading, useCases }: UseCaseCardProps) => {
  return (
    <div>
      <div css={useCaseCardContainerStyles}>
        <H3 css={useCaseCardHeadingStyles}>{heading}</H3>
        <div css={useCaseCardUseCasesContainerStyles}>
          {useCases.map((useCase) => (
            <div key={useCase.title} css={useCaseCardUseCaseStyles}>
              {useCase.image && (
                <img src={useCase.image.url} alt={useCase.image.altText} width={64} height={64} />
              )}
              <H5 css={useCaseCardUseCaseTitleStyles}>{useCase.title}</H5>
              <BodyNormal css={useCaseCardUseCaseDescriptionStyles}>
                {useCase.description}
              </BodyNormal>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
